/**
 * 购物车（纯函数，可单元测试）。
 *
 * 语义和手机扫码端的 `apps/miniprogram/utils/cart.js` **完全一致**，
 * 因为顾客面对的是同一家店、同一份菜单，两个入口的算法必须一样：
 *   1. 「同一道菜 + 同一规格组合」算同一行；规格不同就分成两行
 *      （要一份大份一份小份是常事）。
 *   2. 规格 id 先排序再拼 key，所以「先选大份再选加蛋」和「先选加蛋再选大份」是同一行。
 *   3. 金额一律整数分，用 @jinhua/shared 里那份被 31 项测试覆盖过的函数。
 *   4. 这里算出来的钱**只是给顾客看的过渡数字**，最终金额一律以服务端试算为准。
 */
import { mulCents, sumCents, type Cents } from '@jinhua/shared';

/** 单项最大份数，和后端保持一致 */
export const MAX_QUANTITY = 99;
/** 最多多少行，和后端的 MAX_ORDER_ITEMS 保持一致 */
export const MAX_ROWS = 50;

export interface CartItem {
  /** 行唯一键：productId + 排序后的规格 id */
  key: string;
  productId: string;
  productName: string;
  variantIds: string[];
  /** 规格中文描述，例如「大份 / 加牛肉」 */
  variantText: string | null;
  /** 含规格加价的单价（整数分） */
  unitPriceCents: Cents;
  quantity: number;
  remark: string | null;
  imageUrl: string | null;
}

export type Cart = readonly CartItem[];

export interface AddItemInput {
  productId: string;
  productName: string;
  variantIds?: string[];
  variantText?: string | null;
  unitPriceCents: Cents;
  quantity?: number;
  remark?: string | null;
  imageUrl?: string | null;
}

/** 行唯一键 */
export function makeKey(productId: string, variantIds: readonly string[] = []): string {
  return `${productId}|${[...variantIds].sort().join(',')}`;
}

function clampQuantity(quantity: number): number {
  return Math.min(quantity, MAX_QUANTITY);
}

/** 加一项（同规格累加数量） */
export function addItem(cart: Cart, input: AddItemInput): CartItem[] {
  if (!input?.productId) throw new Error('加入购物车缺少菜品编号');
  const quantity = input.quantity ?? 1;
  if (!Number.isSafeInteger(quantity) || quantity < 1) {
    throw new Error('数量必须是大于 0 的整数');
  }
  if (!Number.isSafeInteger(input.unitPriceCents) || input.unitPriceCents < 0) {
    throw new Error('单价必须是非负整数分');
  }

  const key = makeKey(input.productId, input.variantIds ?? []);
  const next = cart.map((row) => ({ ...row }));
  const existing = next.find((row) => row.key === key);

  if (existing) {
    existing.quantity = clampQuantity(existing.quantity + quantity);
    // 备注以最新一次为准（顾客改主意了）
    if (input.remark != null) existing.remark = input.remark;
    return next;
  }

  if (next.length >= MAX_ROWS) {
    throw new Error(`一次最多点 ${MAX_ROWS} 项菜品`);
  }

  next.push({
    key,
    productId: input.productId,
    productName: input.productName,
    variantIds: [...(input.variantIds ?? [])],
    variantText: input.variantText ?? null,
    unitPriceCents: input.unitPriceCents,
    quantity: clampQuantity(quantity),
    remark: input.remark ?? null,
    imageUrl: input.imageUrl ?? null,
  });
  return next;
}

/** 改数量：减到 0 就自动移除该行 */
export function setQuantity(cart: Cart, key: string, quantity: number): CartItem[] {
  if (!Number.isSafeInteger(quantity)) throw new Error('数量必须是整数');
  if (quantity <= 0) return removeItem(cart, key);
  return cart.map((row) => (row.key === key ? { ...row, quantity: clampQuantity(quantity) } : { ...row }));
}

/** 数量 +1 / -1 */
export function changeQuantity(cart: Cart, key: string, delta: number): CartItem[] {
  const current = cart.find((row) => row.key === key)?.quantity ?? 0;
  return setQuantity(cart, key, current + delta);
}

/** 移除一行 */
export function removeItem(cart: Cart, key: string): CartItem[] {
  return cart.filter((row) => row.key !== key).map((row) => ({ ...row }));
}

/** 改备注 */
export function setRemark(cart: Cart, key: string, remark: string | null): CartItem[] {
  return cart.map((row) => (row.key === key ? { ...row, remark: remark || null } : { ...row }));
}

/** 总份数（按钮角标用） */
export function totalQuantity(cart: Cart): number {
  return cart.reduce((sum, row) => sum + row.quantity, 0);
}

/**
 * 合计（整数分）。
 * 只是过渡显示：确认页和金额一律以服务端试算为准。
 */
export function subtotalCents(cart: Cart): Cents {
  return sumCents(cart.map((row) => mulCents(row.unitPriceCents, row.quantity)));
}

/** 从购物车找一行 */
export function findItem(cart: Cart, key: string): CartItem | null {
  return cart.find((row) => row.key === key) ?? null;
}

/**
 * 转成下单/试算接口要的格式。
 *
 * expectedUnitPriceCents 一起带上：顾客在购物车停留太久、老板中途改了价，
 * 服务端能检测出来并让顾客重新确认，而不是按新价默默扣款。
 */
export function toOrderItems(
  cart: Cart,
): Array<{
  productId: string;
  variantIds: string[];
  quantity: number;
  remark?: string;
  expectedUnitPriceCents: number;
}> {
  return cart.map((row) => ({
    productId: row.productId,
    variantIds: row.variantIds,
    quantity: row.quantity,
    ...(row.remark ? { remark: row.remark } : {}),
    expectedUnitPriceCents: row.unitPriceCents,
  }));
}

/** 顾客改完菜单后，把还能下单的行留下、把有问题的行丢掉 */
export function removeByProductIds(cart: Cart, productIds: readonly string[]): CartItem[] {
  const drop = new Set(productIds);
  return cart.filter((row) => !drop.has(row.productId)).map((row) => ({ ...row }));
}
